import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Space, Spin, Table, Tag, Typography } from "antd";
import { mergeApi, errMsg } from "../institutionMerge.api";
import { CONFIG_LABELS } from "../institutionMerge.constants";
import { FullRecordModal } from "./FullRecordModal";
import {
  After,
  Before,
  EXTRA_LABELS,
  HIDDEN_FIELDS,
  ProvenanceTag,
  afterCol,
  beforeCol,
  same,
} from "./recordDisplay";

const { Text } = Typography;
// every source gets its column (no cap); the cards are fetched at most 7 at a time
const PARALLEL = 7;
const CHANGED_BG = "#fffbe6";

/** Row order: the config meta first (or CONFIG_LABELS), then every other field the cards carry. */
const fieldRows = (cards, meta) => {
  const order = (meta && meta.length
    ? meta.map(m => m.field)
    : ["name", ...Object.keys(CONFIG_LABELS)]
  ).filter(f => !HIDDEN_FIELDS.includes(f));
  const labels = {};
  (meta || []).forEach(m => {
    labels[m.field] = m.label;
  });
  const seen = new Set(order.concat(HIDDEN_FIELDS));
  cards.forEach(c => {
    ((c && c.fields) || []).forEach(f => {
      if (!seen.has(f.path)) {
        seen.add(f.path);
        order.push(f.path);
      }
      if (!labels[f.path] && f.label && f.label !== f.path)
        labels[f.path] = f.label;
    });
  });
  return order.map(path => ({
    key: path,
    path,
    label: labels[path] || CONFIG_LABELS[path] || EXTRA_LABELS[path] || path,
  }));
};

const fieldOf = (card, path) =>
  card && Array.isArray(card.fields)
    ? card.fields.find(f => f.path === path)
    : undefined;

/**
 * The institutions comparison grid of the history: rows = every institution field, one column
 * per merged institution (what it had — avant), last column = the resulting institution (après)
 * with where each value came from. Built from the complete cards of the sources
 * (GET …/documents/Institution/:id, every source, ≤ 7 requests at a time). Degrades to `fallback` when the cards
 * cannot be loaded (e.g. the merge has not been applied yet).
 * Props: merge, preview, onOpenJournal?(seq), fallback? (node shown when no card is available).
 */
export function InstitutionGrid({ merge, preview, onOpenJournal, fallback }) {
  const applied = !["draft", "previewed"].includes(merge.status);
  const sources = preview.sources || [];
  const existing = preview.existingTarget;
  const cols = existing
    ? sources.concat([{ ...existing, isExistingTarget: true }])
    : sources;
  // start in the loading state so the first paint is a Spin, not a flash of « indisponible »
  const [s, setS] = useState({
    loading: applied && cols.length > 0,
    cards: null,
    error: null,
  });
  const [card, setCard] = useState(null);

  const load = useCallback(async () => {
    if (!applied || !cols.length) return;
    setS({ loading: true, cards: null, error: null });
    const results = new Array(cols.length);
    let next = 0;
    const worker = async () => {
      while (next < cols.length) {
        const i = next++;
        try {
          results[i] = {
            status: "fulfilled",
            value: await mergeApi.document(
              merge._id,
              "Institution",
              cols[i]._id,
            ),
          };
        } catch (reason) {
          results[i] = { status: "rejected", reason };
        }
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(PARALLEL, cols.length) }, worker),
    );
    const cards = results.map(r => (r.status === "fulfilled" ? r.value : null));
    const failed = results.filter(r => r.status === "rejected");
    setS({
      loading: false,
      cards,
      error:
        failed.length === results.length
          ? errMsg(failed[0].reason)
          : failed.length
          ? `${failed.length} fiche(s) indisponible(s) : ${errMsg(
              failed[0].reason,
            )}`
          : null,
    });
    /* eslint-disable-next-line */
  }, [merge._id, applied, cols.map(c => c._id).join(",")]);
  useEffect(() => {
    load();
  }, [load]);

  if (!applied)
    return (
      <Text type="secondary">
        La comparaison champ par champ (chaque établissement avant → institution
        résultante après) sera disponible une fois la fusion appliquée.
      </Text>
    );
  if (s.loading)
    return (
      <div style={{ textAlign: "center", padding: 24 }}>
        <Spin tip="Chargement des fiches complètes des établissements…">
          <div style={{ height: 40 }} />
        </Spin>
      </div>
    );
  const cards = s.cards || [];
  if (!cards.some(Boolean))
    return (
      <Space direction="vertical" style={{ width: "100%" }}>
        <Alert
          type="warning"
          showIcon
          message="Comparaison champ par champ indisponible"
          description={s.error}
          action={
            <Button size="small" onClick={load}>
              Réessayer
            </Button>
          }
        />
        {fallback}
      </Space>
    );

  const meta = (preview.config && preview.config.meta) || [];
  const rows = fieldRows(cards, meta);
  const withTarget = cards.find(c => c && c.target);
  const target = withTarget ? withTarget.target : null;
  const tFields = (target && target.fields) || {};
  const tProv = (target && target.provenance) || {};
  const labelsOf = i => (cards[i] && cards[i].labels) || {};
  const afterValue = path =>
    target && Object.prototype.hasOwnProperty.call(tFields, path)
      ? tFields[path]
      : undefined;
  const hasAfter = path =>
    target && Object.prototype.hasOwnProperty.call(tFields, path);
  const beforeValue = (i, path) => {
    const f = fieldOf(cards[i], path);
    return f ? f.before : undefined;
  };
  const rowChanged = r =>
    hasAfter(r.path) &&
    cards.some(
      (c, i) => c && !same(beforeValue(i, r.path), afterValue(r.path)),
    );

  const columns = [
    {
      title: "Champ",
      dataIndex: "label",
      width: 190,
      fixed: "left",
      render: (l, r) => <Text strong={rowChanged(r)}>{l}</Text>,
    },
    ...cols.map((c, i) => ({
      title: (
        <Space direction="vertical" size={0}>
          <span>
            {c.name}{" "}
            <Text type="secondary" style={{ fontWeight: "normal" }}>
              ({c.isExistingTarget ? "avant, cible existante" : "avant"})
            </Text>
          </span>
          {cards[i] && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: "auto", fontWeight: "normal" }}
              onClick={() =>
                setCard({
                  model: "Institution",
                  documentId: cards[i].documentId || c._id,
                  label: cards[i].label || c.name,
                })
              }
            >
              Fiche complète
            </Button>
          )}
        </Space>
      ),
      key: `src:${c._id}`,
      ...beforeCol,
      render: (_, r) => {
        if (!cards[i])
          return (
            <Text type="secondary" italic>
              fiche indisponible
            </Text>
          );
        const v = beforeValue(i, r.path);
        const changed = hasAfter(r.path) && !same(v, afterValue(r.path));
        return <Before value={v} changed={changed} labels={labelsOf(i)} />;
      },
    })),
    ...(target
      ? [
          {
            title: (
              <Space direction="vertical" size={0}>
                <span>
                  {target.label}{" "}
                  <Text type="secondary" style={{ fontWeight: "normal" }}>
                    (après)
                  </Text>
                </span>
                {target.documentId && (
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: "auto", fontWeight: "normal" }}
                    onClick={() =>
                      setCard({
                        model: "Institution",
                        documentId: target.documentId,
                        label: target.label,
                      })
                    }
                  >
                    Fiche complète
                  </Button>
                )}
              </Space>
            ),
            key: "after",
            ...afterCol,
            render: (_, r) => {
              if (!hasAfter(r.path)) return <Text type="secondary">—</Text>;
              const labels = withTarget.labels || {};
              return (
                <Space size={4} wrap>
                  <After
                    value={afterValue(r.path)}
                    changed={rowChanged(r)}
                    labels={labels}
                  />
                  <ProvenanceTag value={tProv[r.path]} />
                </Space>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Text strong>
        Comparaison champ par champ — chaque établissement (avant) → institution
        résultante (après)
      </Text>
      {!target && (
        <Text type="secondary">
          Institution résultante : fiche indisponible (supprimée à la
          restauration ?) — seules les valeurs d'avant sont affichées.
        </Text>
      )}
      {s.error && <Alert type="warning" showIcon message={s.error} />}
      <Table
        size="small"
        pagination={false}
        dataSource={rows}
        columns={columns}
        scroll={{ x: true }}
        onRow={r => ({
          style: rowChanged(r) ? { background: CHANGED_BG } : undefined,
        })}
        rowClassName={r =>
          rowChanged(r) ? "merge-row-changed" : "merge-row-unchanged"
        }
      />
      <Text type="secondary">
        <Tag color="gold">surligné</Tag> = au moins un établissement avait une
        autre valeur que le résultat ; valeurs d'avant barrées quand elles ont
        changé, valeur d'après en gras.
      </Text>
      {card && (
        <FullRecordModal
          open
          mergeId={merge._id}
          model={card.model}
          documentId={card.documentId}
          label={card.label}
          onClose={() => setCard(null)}
          onOpenJournal={onOpenJournal}
        />
      )}
    </Space>
  );
}
